import get from 'lodash/get';
import PropTypes from 'prop-types';
import debounce from 'lodash/debounce';
import React, { useMemo, useState, useRef, useCallback } from 'react';
import { enhance } from '../enhance';

import Button from '../../../Button';
import { usePCPField } from './hooks';
import SelectCustom from '../../../SelectCustom';
import { makeOptionalLabel } from '../../../../utils/makeOptionalLabel';

function HealthInformationForm({
  details,
  updateData,
  setHealthInfo,
  medicationSearch,
  fetchMedications,
  loadMoreMedications,
  healthConditionsList,
  clearMedicationSearch,
  medicationAllergiesList,
  intl: { formatMessage },
  pcpProviderAnswer,
}) {
  const offsetRef = useRef(0);
  const { primaryPatientProfile, id } = details;
  const [medicationQuery, setMedicationQuery] = useState('');
  const { healthConditions, medicationAllergies } = primaryPatientProfile || {};
  const patientProfileNdcDrugs = get(
    primaryPatientProfile,
    'patientProfileNdcDrugs',
    [],
  );
  const [allegries, setAllergies] = useState(medicationAllergies);
  const [currentMedications, setCurrentMedications] = useState(
    patientProfileNdcDrugs,
  );
  const [healthConditionsValue, setHealthConditionsValue] = useState(
    healthConditions,
  );

  const handleNext = e => {
    e.preventDefault();

    setHealthInfo({
      pcpProviderAnswer: pcpAnswer || pcpProviderAnswer,
      medicationAllergies: allegries,
      ndcDrugIds: currentMedications,
      healthConditions: healthConditionsValue,
    });

    updateData();
  };

  const handleMedicationsSearch = debounce(text => {
    offsetRef.current = 0;
    clearMedicationSearch();

    if (text.length < 3) return;
    setMedicationQuery(text);

    fetchMedications({
      userId: id,
      limit: 10,
      query: text,
      offset: offsetRef.current,
    });
  }, 1000);

  const loadMore = useCallback(
    debounce(() => {
      loadMoreMedications({
        userId: id,
        query: medicationQuery,
        offset: offsetRef.current,
      });
      offsetRef.current += 10;
    }, 100),
    [id, medicationQuery],
  );

  const getSafeNdcDrugs = patientProfileNdcDrugs.map(drug => ({
    id: drug.ndcDrugId,
    name: drug.displayableName,
  }));

  const filterFormSelectOptions = selectData =>
    selectData.filter(el => el.value.enabled);

  const renderMedicationSearch = useMemo(
    () => (
      <SelectCustom
        multi
        className="mt3"
        id="currentMedications"
        options={medicationSearch}
        defaultValue={getSafeNdcDrugs}
        onMenuScrollToBottom={loadMore}
        onInputChange={handleMedicationsSearch}
        onChange={select =>
          setCurrentMedications(select.map(el => el.value.id))
        }
        label={makeOptionalLabel('common.currentMedications', formatMessage)}
        placeholder={formatMessage({
          id: 'common.currentMedications',
        })}
      />
    ),
    [getSafeNdcDrugs, medicationSearch.length],
  );

  const { pcpAnswer, renderPcpField, shouldContinue } = usePCPField({
    details,
    formatMessage,
    pcpProviderAnswer,
  });

  return (
    <form onSubmit={handleNext}>
      {renderPcpField}
      <SelectCustom
        multi
        className="mt3"
        id="healthConditions"
        options={filterFormSelectOptions(healthConditionsList)}
        defaultValue={healthConditionsValue}
        onChange={select =>
          setHealthConditionsValue(select.map(el => el.value))
        }
        label={makeOptionalLabel(
          'profile.fields.healthConditions',
          formatMessage,
        )}
        placeholder={formatMessage({ id: 'profile.fields.healthConditions' })}
      />
      <SelectCustom
        multi
        className="mt3"
        id="medicationAllergies"
        defaultValue={allegries}
        options={filterFormSelectOptions(medicationAllergiesList)}
        onChange={select => setAllergies(select.map(el => el.value))}
        label={makeOptionalLabel(
          'profile.fields.medicationAllergies',
          formatMessage,
        )}
        placeholder={formatMessage({
          id: 'profile.fields.medicationAllergies',
        })}
      />
      {renderMedicationSearch}

      <div className="mt4 tr">
        <Button raised color="primary" disabled={!shouldContinue}>
          {formatMessage({ id: 'common.next' })}
        </Button>
      </div>
    </form>
  );
}

HealthInformationForm.propTypes = {
  intl: PropTypes.object,
  details: PropTypes.object,
  updateData: PropTypes.func,
  setHealthInfo: PropTypes.func,
  fetchMedications: PropTypes.func,
  medicationSearch: PropTypes.array,
  loadMoreMedications: PropTypes.func,
  healthConditionsList: PropTypes.array,
  clearMedicationSearch: PropTypes.func,
  medicationAllergiesList: PropTypes.array,
  pcpProviderAnswer: PropTypes.string,
};

export default enhance(HealthInformationForm);



// WEBPACK FOOTER //
// ./src/components/AppointmentForm/AppointmentHealthInformation/HealthInformationForm/index.jsx